import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard } from "@/components/common";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell, PieChart, Pie, Legend
} from "recharts";

const COLORS = ["#f59e0b", "#60a5fa", "#10b981", "#fbbf24", "#94a3b8", "#ef4444", "#d97706"];

const axisStyle = { fontSize: 11, fill: "var(--ink-mute)", fontFamily: "JetBrains Mono" };
const gridStroke = "var(--line)";

function ChartCard({ title, subtitle, height = 280, children }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ marginBottom: 14 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: "var(--ink-dim)", marginTop: 4 }}>{subtitle}</div>}
      </div>
      <div style={{ width: "100%", height, minHeight: height }}>{children}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--ink-mute)", fontSize: 13 }}>{msg}</div>;
}

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/analytics/${user.role}`).then(r => setData(r.data)).catch(() => setData({}));
  }, [user.role]);
  if (!data) return <div style={{ color: "var(--ink-mute)" }}>Loading analytics…</div>;
  if (user.role === "buyer") return <BuyerAnalytics data={data} />;
  if (user.role === "supplier") return <SupplierAnalytics data={data} />;
  return <AdminAnalytics data={data} />;
}

function BuyerAnalytics({ data }) {
  return (
    <>
      <PageHeader title="Procurement Analytics" subtitle="Spend, cycle time and sourcing patterns." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
        <KpiCard label="Total Spend" value={`$${(data.total_spend || 0).toLocaleString()}`} sub="CAD · lifetime" accent="var(--amber-400)" />
        <KpiCard label="Avg Cycle Time" value={`${data.avg_cycle_days}d`} sub="create → award" />
        <KpiCard label="RFQs Issued" value={data.rfqs_total} />
        <KpiCard label="Orders" value={data.orders_total} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <ChartCard title="SPEND OVER TIME" subtitle="Monthly procurement spend (CAD)">
          {data.spend_series?.length ? (
            <ResponsiveContainer>
              <LineChart data={data.spend_series} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={axisStyle} axisLine={{ stroke: gridStroke }} tickLine={false} />
                <YAxis tick={axisStyle} axisLine={{ stroke: gridStroke }} tickLine={false} />
                <Tooltip contentStyle={{ background: "var(--navy-800)", border: "1px solid var(--line)", fontSize: 12, borderRadius: 4 }} />
                <Line type="monotone" dataKey="spend" stroke="#f59e0b" strokeWidth={2} dot={{ fill: "#f59e0b", r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty msg="No spend data yet. Award an RFQ to see trends here." />}
        </ChartCard>
        <ChartCard title="SPEND BY CATEGORY">
          {data.by_category?.length ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.by_category} dataKey="spend" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={{ fill: "var(--ink-dim)", fontSize: 11 }}>
                  {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "var(--navy-800)", border: "1px solid var(--line)", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty msg="No category spend yet." />}
        </ChartCard>
      </div>
    </>
  );
}

function SupplierAnalytics({ data }) {
  return (
    <>
      <PageHeader title="Performance Analytics" subtitle="Win rate, category demand and revenue trends." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
        <KpiCard label="Matched RFQs" value={data.rfqs_matched} />
        <KpiCard label="Quotes Sent" value={data.quotes_sent} />
        <KpiCard label="Win Rate" value={`${data.win_rate}%`} accent="var(--amber-400)" />
        <KpiCard label="Revenue" value={`$${(data.revenue_total || 0).toLocaleString()}`} sub="CAD · lifetime" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <ChartCard title="REVENUE OVER TIME" subtitle="Monthly closed orders (CAD)">
          {data.revenue_series?.length ? (
            <ResponsiveContainer>
              <LineChart data={data.revenue_series} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={axisStyle} axisLine={{ stroke: gridStroke }} tickLine={false} />
                <YAxis tick={axisStyle} axisLine={{ stroke: gridStroke }} tickLine={false} />
                <Tooltip contentStyle={{ background: "var(--navy-800)", border: "1px solid var(--line)", fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty msg="Win an RFQ to see your revenue trend." />}
        </ChartCard>
        <ChartCard title="QUOTE VOLUME BY CATEGORY">
          {data.category_demand?.length ? (
            <ResponsiveContainer>
              <BarChart data={data.category_demand.slice(0, 6)} layout="vertical" margin={{ left: 12 }}>
                <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={axisStyle} axisLine={{ stroke: gridStroke }} />
                <YAxis type="category" dataKey="category" tick={{ ...axisStyle, fontSize: 10 }} width={130} axisLine={{ stroke: gridStroke }} />
                <Tooltip contentStyle={{ background: "var(--navy-800)", border: "1px solid var(--line)", fontSize: 12 }} />
                <Bar dataKey="quotes" fill="#f59e0b" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty msg="No quotes sent yet." />}
        </ChartCard>
      </div>
    </>
  );
}

function AdminAnalytics({ data }) {
  return (
    <>
      <PageHeader title="Platform Analytics" subtitle="GMV, funnel and supplier ranking." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
        <KpiCard label="Platform GMV" value={`$${(data.gmv_total || 0).toLocaleString()}`} sub="CAD · lifetime" accent="var(--amber-400)" />
        <KpiCard label="Active Buyers" value={data.buyers} />
        <KpiCard label="Active Suppliers" value={data.suppliers} />
        <KpiCard label="Pending KYC" value={data.pending_verifications} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 16 }}>
        <ChartCard title="GMV OVER TIME">
          {data.gmv_series?.length ? (
            <ResponsiveContainer>
              <LineChart data={data.gmv_series} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={axisStyle} axisLine={{ stroke: gridStroke }} tickLine={false} />
                <YAxis tick={axisStyle} axisLine={{ stroke: gridStroke }} tickLine={false} />
                <Tooltip contentStyle={{ background: "var(--navy-800)", border: "1px solid var(--line)", fontSize: 12 }} />
                <Line type="monotone" dataKey="gmv" stroke="#f59e0b" strokeWidth={2} dot={{ fill: "#f59e0b", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty msg="No transactions yet." />}
        </ChartCard>
        <ChartCard title="RFQ FUNNEL">
          <ResponsiveContainer>
            <BarChart data={[
              { stage: "Created", count: data.rfq_total },
              { stage: "Quoted", count: data.rfq_quoted },
              { stage: "Awarded", count: data.rfq_awarded },
            ]}>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="stage" tick={axisStyle} axisLine={{ stroke: gridStroke }} />
              <YAxis tick={axisStyle} axisLine={{ stroke: gridStroke }} />
              <Tooltip contentStyle={{ background: "var(--navy-800)", border: "1px solid var(--line)", fontSize: 12 }} />
              <Bar dataKey="count" fill="#60a5fa" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <div className="card" style={{ padding: 20 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)", marginBottom: 14 }}>TOP SUPPLIERS BY REVENUE</div>
        {data.top_suppliers?.length ? (
          <table className="data">
            <thead><tr><th>#</th><th>Supplier</th><th>Revenue (CAD)</th></tr></thead>
            <tbody>{data.top_suppliers.map((s, i) => (
              <tr key={i}><td className="font-mono" style={{ color: "var(--amber-400)" }}>#{i + 1}</td><td>{s.name}</td><td style={{ fontWeight: 600 }}>${s.revenue.toLocaleString()}</td></tr>
            ))}</tbody>
          </table>
        ) : <Empty msg="No supplier revenue data yet." />}
      </div>
    </>
  );
}
